import type { SecurityClearance } from "@fairfleet/geotab";
import { useSecurityClearances } from "./useSecurityClearances";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { buildGroupIndex, groupAncestry, GroupIndex } from "@/utilities/groupIndex";

function toClearanceIndex(clearances: SecurityClearance[]): GroupIndex<SecurityClearance> {
  return buildGroupIndex(clearances);
}

/**
 * The decision for one clearance: the nearest clearance in its ancestry (itself first) whose
 * securityFilters mention `filterId` decides; `undefined` when none mentions it.
 */
function resolveFilter(
  clearanceId: string,
  filterId: string,
  index: GroupIndex<SecurityClearance>,
): boolean | undefined {
  for (const clearance of groupAncestry(clearanceId, index)) {
    const filter = clearance.securityFilters?.find(f => f.securityIdentifier === filterId);
    if (filter) return filter.isAdd !== false;
  }
  return undefined;
}

/**
 * Determines whether the current user has a specific permission.
 *
 * Permission resolution rules:
 *   - A user's permission is determined by the clearances they belong to (me.securityGroups)
 *     and the clearances those inherit from.
 *   - Per clearance, the nearest clearance in its ancestry (itself first) whose securityFilters
 *     mention the identifier decides; a parent's removal is inherited unless a child adds it back.
 *   - If ANY of the user's clearances resolves to removed (isAdd === false), the permission is
 *     denied, regardless of the others.
 *
 * Fails closed: while loading, without a signed-in user, or when this session's clearances
 * could not be loaded, the permission is treated as not granted. A failed background refetch
 * keeps the decision from the clearances already loaded in this session and reports `isError`.
 */
export function useHasPermission(filterId: string): {
  hasPermission: boolean;
  isLoading: boolean;
  isError: boolean;
  /** True once this session's clearances have loaded; false after a failed first load. */
  isResolved: boolean;
  /** A (re)load is running, e.g. after `retry` was pressed on a failed first load. */
  isFetching: boolean;
  /** Loads the clearances again, e.g. after a failed first load. */
  retry: () => void;
} {
  const { me } = useGeotabClient();
  // `index` is undefined until this session's clearances have loaded once. A failed background
  // refetch keeps the last loaded clearances (TanStack keeps `data` on a refetch error), so the
  // decision stays and only `isError` turns true; a new session has its own key and starts empty.
  const { data: index, isLoading, isError, isFetching, refetch } = useSecurityClearances(toClearanceIndex);
  const retry = () => void refetch();

  if (!me || !index) {
    return { hasPermission: false, isLoading, isError, isFetching, isResolved: false, retry };
  }

  const denied = (me.securityGroups ?? []).some(
    group => resolveFilter(group.id, filterId, index) === false,
  );

  return { hasPermission: !denied, isLoading, isError, isFetching, isResolved: true, retry };
}
